/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { GraduationCap, ChevronDown, ChevronUp, X, Music2, Guitar, ListMusic, MessageCircle, Navigation, Target, Settings } from 'lucide-react';
import { Button } from './ui/button';

interface MobileTutorialPanelProps {
  appMode: 'scale' | 'chord' | 'earTraining' | 'compare';
  currentKey?: string;
  currentScale?: string;
  onDismiss?: () => void;
}

/**
 * MobileTutorialPanel - Step-by-step tutorial content for mobile
 *
 * Displays above Learning Tips panel
 * Provides feature walkthroughs and how-to guidance
 */
const MobileTutorialPanel: React.FC<MobileTutorialPanelProps> = ({
  appMode,
  currentKey,
  currentScale,
  onDismiss
}) => {
  const [isExpanded, setIsExpanded] = useState(() => {
    // Check localStorage for user preference, default to collapsed
    const saved = localStorage.getItem('mobileTutorialPanelExpanded');
    return saved === 'true'; // Default to false (collapsed)
  });

  // Save expansion state to localStorage
  useEffect(() => {
    localStorage.setItem('mobileTutorialPanelExpanded', String(isExpanded));
  }, [isExpanded]);

  const handleDismiss = () => {
    if (onDismiss) {
      onDismiss();
    }
    // Also save to localStorage
    localStorage.setItem('mobileTutorialPanelDismissed', 'true');
  };

  // Mode-specific tutorial content
  const getModeContent = () => {
    if (appMode === 'scale') {
      return {
        icon: <Music2 className="h-4 w-4" />,
        title: 'Scale Mode Tutorial',
        sections: [
          {
            icon: <Navigation className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />,
            heading: 'Navigation',
            text: 'Tap the down arrow at the top to show the control panel where you can switch between Scales, Chords, and Practice modes. The Chords section lets you explore chord shapes and fingerings. Practice offers ear training and fretboard quizzes.'
          },
          {
            icon: <Settings className="h-3.5 w-3.5 text-slate-600 dark:text-slate-400" />,
            heading: 'Instrument Setup',
            text: 'Tap the gear icon at the top to customize your instrument - change tuning, number of strings, fret count, and orientation. This ensures the fretboard matches your actual guitar or bass.'
          },
          {
            icon: <Music2 className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" />,
            heading: 'What You See',
            text: 'The fretboard shows all the notes in your selected scale, color-coded by interval. The root note (1) is highlighted to help you orient yourself.'
          },
          {
            icon: <ListMusic className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />,
            heading: 'Using Jam Tracks',
            text: 'Tap "Jam Tracks" above to play along with backing tracks in your key. The fretboard shows chord changes in real-time. Start by playing root notes on the chord changes, then experiment with other scale tones.'
          },
          {
            icon: <Guitar className="h-3.5 w-3.5 text-orange-600 dark:text-orange-400" />,
            heading: 'Finding Patterns',
            text: 'Notice how the same color pattern repeats across the fretboard? These are your scale positions. Try playing through one position, then find the same pattern starting on a different string.'
          },
          {
            icon: <MessageCircle className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />,
            heading: 'Get Help',
            text: 'Tap the chat icon to ask the Harmonic Companion questions like "What chords work with this scale?" or "Show me a blues progression in this key."'
          }
        ]
      };
    } else if (appMode === 'chord') {
      return {
        icon: <Guitar className="h-4 w-4" />,
        title: 'Chord Mode Tutorial',
        sections: [
          {
            icon: <Navigation className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />,
            heading: 'Navigation',
            text: 'Tap the down arrow at the top to show the control panel where you can switch between Chords, Scales, and Practice modes. The Scales section shows scales that work with this chord. Practice offers ear training and chord recognition exercises.'
          },
          {
            icon: <Settings className="h-3.5 w-3.5 text-slate-600 dark:text-slate-400" />,
            heading: 'Instrument Setup',
            text: 'Tap the gear icon at the top to customize your instrument - change tuning, number of strings, fret count, and orientation. This ensures the fretboard matches your actual guitar or bass.'
          },
          {
            icon: <Music2 className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" />,
            heading: 'What You See',
            text: 'The fretboard displays every location where you can play the notes of your chord. This helps you understand the chord across the entire neck, not just one position.'
          },
          {
            icon: <Guitar className="h-3.5 w-3.5 text-orange-600 dark:text-orange-400" />,
            heading: 'Using Fingerings',
            text: 'Tap a fingering card above to see a specific chord voicing highlighted on the fretboard. Each fingering is a moveable shape - practice it in this key, then move it up or down to play the same chord in different keys.'
          },
          {
            icon: <ListMusic className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />,
            heading: 'Compare Voicings',
            text: 'Try selecting different fingerings to hear how the same chord sounds in different positions. Higher positions sound brighter, lower positions sound fuller. Context matters!'
          },
          {
            icon: <MessageCircle className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />,
            heading: 'Learn More',
            text: 'Ask the Harmonic Companion about chord progressions, substitutions, or music theory. For example: "What scale goes with this chord?" or "Show me a jazz progression."'
          }
        ]
      };
    }
    return null;
  };

  const content = getModeContent();
  if (!content) return null;

  return (
    <div className="w-full mt-2 mb-2 px-3">
      <Card className="bg-gradient-to-br from-purple-50/80 to-violet-50/80 dark:from-purple-950/20 dark:to-violet-950/20 border-purple-200/60 dark:border-purple-800/40 shadow-sm">
        <CardContent className="p-3">
          {/* Header */}
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex items-center gap-2">
              <div className="flex-shrink-0 p-1.5 rounded-md bg-purple-500/10">
                <GraduationCap className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-sm font-semibold text-purple-900 dark:text-purple-100">
                Tutorial
              </h3>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpanded(!isExpanded)}
                className="h-7 w-7 p-0 hover:bg-purple-100/50 dark:hover:bg-purple-900/30"
                aria-label={isExpanded ? "Collapse" : "Expand"}
              >
                {isExpanded ? (
                  <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDismiss}
                className="h-7 w-7 p-0 hover:bg-purple-100/50 dark:hover:bg-purple-900/30"
                aria-label="Dismiss"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {/* Collapsed Preview */}
          {!isExpanded && (
            <p className="text-xs text-muted-foreground">
              Tap to learn how to use {appMode === 'scale' ? 'Scale' : 'Chord'} Mode features
            </p>
          )}

          {/* Expanded Content */}
          {isExpanded && (
            <div className="space-y-3 mt-3">
              {/* Tutorial Sections */}
              {content.sections.map((section, index) => (
                <div key={index} className="flex items-start gap-2">
                  <div className="flex-shrink-0 mt-0.5">
                    {section.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-semibold text-purple-800 dark:text-purple-200">
                      {section.heading}:
                    </span>
                    <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">
                      {section.text}
                    </p>
                  </div>
                </div>
              ))}

              {/* Encouragement */}
              <div className="pt-2 border-t border-purple-200/50 dark:border-purple-800/30">
                <p className="text-xs text-muted-foreground leading-relaxed italic">
                  Take your time exploring each feature. You can always reopen this tutorial if you need a refresher!
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MobileTutorialPanel;
